import { isServer } from '@moonpig/web-core-utils'
import { createMetrics, logger } from '@moonpig/web-core-monitoring'
import { EppoConfiguration } from '../../types'
import { createEppoClient } from '../../browser'

const EPPO_REFRESH_INTERVAL = 30000
const EPPO_TIMEOUT = 1000
let eppoConfiguration: EppoConfiguration

// The contentfulConfiguration only exists to query the experiments tagged with `Contentful`.
// This seems the only reliable way of understanding which experiments to pass through
// automatically to the content-api. It is not used outside of `getContentfulExperimentNames`.
//
// A query has been raised with Eppo around being able to see experiment tags without needing to do
// the above.
let contentfulConfiguration: EppoConfiguration

let lastFetch: Date

export const getEppoClient = async () => {
  if (isServer) {
    const shouldFetch = lastFetch
      ? new Date().getTime() - lastFetch.getTime() >= EPPO_REFRESH_INTERVAL
      : true

    if (shouldFetch) {
      ;[eppoConfiguration, contentfulConfiguration] = await Promise.all([
        fetchEppoConfiguration(process.env.EPPO_API_SDK_KEY),
        fetchEppoConfiguration(process.env.EPPO_CONTENTFUL_API_SDK_KEY),
      ])
    }
  }

  const getContentfulExperimentNames = () => {
    return Object.values(contentfulConfiguration ?? {}).reduce(
      (acc, flag) => (flag.enabled ? [...acc, flag.key] : acc),
      [] as string[],
    )
  }

  const client = createEppoClient({
    flagsConfiguration: eppoConfiguration,
  })

  return { client, eppoConfiguration, getContentfulExperimentNames }
}

const fetchEppoConfiguration = async (apiKey: string | undefined) => {
  const eppoUrl = new URL('https://fscdn.eppo.cloud/api/flag-config/v1/config')
  eppoUrl.search = new URLSearchParams({
    apiKey: `${apiKey}`,
    sdkName: 'node-server-sdk',
    sdkVersion: '3.5.0',
  }).toString()

  const abortController = new AbortController()
  const timerId = setTimeout(() => {
    logger.fixToday(`EPPO reached timeout of ${EPPO_TIMEOUT}ms`)
    abortController.abort()
  }, EPPO_TIMEOUT)
  const { traceAsync, count } = createMetrics({ useTracing: false })

  try {
    const res = await traceAsync(
      { metricName: 'fetch-eppo-configuration' },
      async () => fetch(eppoUrl, { signal: abortController.signal }),
    )

    if (!res.ok) {
      throw new Error(`Failed to fetch EPPO configuration: ${res.statusText}`)
    }

    const json = await res.json()

    if (!json.flags) {
      throw new Error('Failed to fetch EPPO configuration: no flags found')
    }

    lastFetch = new Date()
    return json.flags
  } catch (error) {
    logger.fixToday('Failed to fetch EPPO configuration', error)
    if (!eppoConfiguration) {
      count({ metricName: 'empty-eppo-configuration' })
      return {}
    }
    return eppoConfiguration
  } finally {
    clearTimeout(timerId)
  }
}
